import useCarouselContext from '@/context/CarouselContext';
import { ShopifyVariant } from '@/lib/shopify';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

const VARIANT_BASE_URL = 'gid://shopify/ProductVariant/';

export const useVariantSelection = (cb?: (variant: ShopifyVariant) => void) => {
  const { setCarouselImagePosition } = useCarouselContext() || {};

  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previousVariantRef = useRef<string | null>(null); // Track the last selected variant

  const handleVariantClick = useCallback(
    (variant: ShopifyVariant) => {
      const variantId = variant.id.split(VARIANT_BASE_URL)[1];

      // Only proceed if the selected variant is different from the previous one
      if (previousVariantRef.current !== variantId) {
        const params = new URLSearchParams(searchParams || '');

        // Ensure setCarouselImagePosition exists before calling it
        if (setCarouselImagePosition) setCarouselImagePosition(0); // Always set to the first image

        params.set('variant', variantId);
        push(`${pathname}?${params.toString()}`, undefined, { scroll: false });

        // Update the previous variant to avoid redundant changes
        previousVariantRef.current = variantId;
        cb?.(variant);
      }
    },
    [cb, pathname, push, setCarouselImagePosition, searchParams],
  );

  useEffect(() => {
    // Set initial carousel image position once when the component mounts
    if (setCarouselImagePosition) setCarouselImagePosition(0);
  }, [setCarouselImagePosition]);

  return {
    handleVariantClick,
  };
};
